import { PurchaseOrderStatusLabels } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../types';
import {
	buildPurchaseOrderColumns,
	PURCHASE_ORDER_REQUIRED_FIELDS,
} from './purchase-order-columns';

/**
 * Purchase orders embedded on an item's detail page (PD-15581) — every PO
 * carrying a line for the item, so a buyer looking at an item's quantity on
 * order can see which orders it is coming in on without searching.
 *
 * The item scope itself is pinned by the tab's queryFn (the
 * `purchaseOrderLineItem` filter), not by a locked chip, so it never shows as a
 * removable filter. The default tab shows only OPEN orders — issued, partially
 * received or backordered — which is what "on order" means; the second tab
 * lifts that restriction. Same column set as the other purchase-order lists
 * plus the item's own ordered / received / expected figures on each PO.
 */
const OPEN_STATUS_DEFAULT = [
	{ id: '3', label: PurchaseOrderStatusLabels['3'] },
	{ id: '4', label: PurchaseOrderStatusLabels['4'] },
	{ id: '8', label: PurchaseOrderStatusLabels['8'] },
];

export const PURCHASE_ORDER_ITEM_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-orders-item-open',
			cacheTTL: 300000,
			name: 'Open Purchase Orders',
			type: 'default',
			active: true,
			columns: buildPurchaseOrderColumns({ includeItemLine: true }),
			description:
				'Purchase orders with this item that have been issued and not yet fully received.',
			defaultSortId: 'column-order-date-desc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 0,
			filterRules: {
				purchaseOrderStatus: {
					defaultValue: OPEN_STATUS_DEFAULT,
				},
			},
		},
		{
			id: 'default-purchase-orders-item-all',
			cacheTTL: 300000,
			name: 'All Purchase Orders',
			type: 'default',
			active: true,
			columns: buildPurchaseOrderColumns({ includeItemLine: true }),
			description: 'Every purchase order with this item, in any status.',
			defaultSortId: 'column-order-date-desc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 1,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'purchaseOrderKeywords',
			'purchaseOrderStatus',
			'purchaseOrderVendor',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/purchase-orders/:id',
			objectLabel: 'Purchase Order',
			objectTable: 'purchase_order',
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'date', fieldLabel: 'Order Date' },
				{ fieldName: 'requestedDate', fieldLabel: 'Requested Date' },
				{ fieldName: 'dateExpected', fieldLabel: 'Date Expected' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
		{
			// Registers the vendor object so the Vendor column's object-link
			// resolves to /vendors/:id rather than the first object's route.
			baseUrl: '/vendors/:id',
			objectLabel: 'Vendor',
			objectTable: 'vendor',
			selectedFields: [],
		},
	],
	tableSorts: [
		{
			id: 'po-number',
			name: 'PO Number',
			fields: [{ direction: 'ASC', fieldName: 'externalId' }],
			description: 'Sort by PO number',
		},
		{
			id: 'status',
			name: 'Status',
			fields: [{ direction: 'ASC', fieldName: 'status' }],
			description: 'Sort by status',
		},
		{
			id: 'order-date',
			name: 'Order Date',
			fields: [{ direction: 'DESC', fieldName: 'date' }],
			description: 'Sort by order date (newest first)',
		},
		{
			id: 'requested-date',
			name: 'Requested Date',
			fields: [{ direction: 'DESC', fieldName: 'requestedDate' }],
			description: 'Sort by requested date (newest first)',
		},
	],
};
